/** Short notifications at the bottom of the screen. */
import { type ReadonlySignal, type Signal, signal } from '@preact/signals';

export type ToastTone = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  id: number;
  text: string;
  tone: ToastTone;
}

export interface ToastOptions {
  tone?: ToastTone;
  durationMs?: number;
  /**
   * News that replaces itself: a toast with the same key that is still on screen takes the
   * new text (and restarts its timer) instead of a second toast piling up below it.
   */
  key?: string;
}

/** More toasts than this push the oldest one out. */
const MAX_VISIBLE = 3;
const DEFAULT_DURATION_MS = 3500;

export class ToastStore {
  private readonly listSignal: Signal<readonly Toast[]> = signal([]);
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();
  /** The toast on screen for each key. */
  private readonly keyed = new Map<string, number>();
  private nextId = 1;

  readonly toasts: ReadonlySignal<readonly Toast[]> = this.listSignal;

  /**
   * Shows a toast and returns its id. Showing a text that is already on screen only restarts
   * its timer, so repeated errors do not pile up.
   */
  show(text: string, options: ToastOptions = {}): number {
    const tone = options.tone ?? 'info';
    const duration = options.durationMs ?? DEFAULT_DURATION_MS;
    const list = this.listSignal.peek();
    const keyedId = options.key === undefined ? undefined : this.keyed.get(options.key);
    const existing =
      list.find((t) => t.id === keyedId) ?? list.find((t) => t.text === text && t.tone === tone);
    const id = existing?.id ?? this.nextId++;
    if (!existing) {
      const next = [...list, { id, text, tone }];
      for (const dropped of next.splice(0, Math.max(0, next.length - MAX_VISIBLE))) {
        this.forget(dropped.id);
      }
      this.listSignal.value = next;
    } else if (existing.text !== text || existing.tone !== tone) {
      this.listSignal.value = list.map((t) => (t.id === id ? { id, text, tone } : t));
    }
    if (options.key !== undefined) this.keyed.set(options.key, id);
    this.clearTimer(id);
    this.timers.set(
      id,
      setTimeout(() => this.dismiss(id), duration),
    );
    return id;
  }

  dismiss(id: number): void {
    this.forget(id);
    const list = this.listSignal.peek();
    if (list.some((t) => t.id === id)) this.listSignal.value = list.filter((t) => t.id !== id);
  }

  private forget(id: number): void {
    this.clearTimer(id);
    for (const [key, keyedId] of this.keyed) if (keyedId === id) this.keyed.delete(key);
  }

  private clearTimer(id: number): void {
    const timer = this.timers.get(id);
    if (timer !== undefined) clearTimeout(timer);
    this.timers.delete(id);
  }
}
